// @givmo/board — the Board Asks (EMC-WEB4 over the backend's EMC-BE9; `board-ask.md`), the
// officers' side: the Board Asks list (`/asks`), and one ask (`/asks/$askId`):
//
// * waiting for approval, or a draft (frame 23): the same form its drafter wrote, which any admin with
//   access to the Board module may change, then "Approve and send", or send it back to its drafter;
// * sent (frame 32): "Who's taking part" — each director it went to, alphabetical, when they opened
//   it and shared, and what their page raised. No ranking and nothing in red.
//
// D-EMC-15 as ruled 2026-10-31 23:00: "An admin with access to the Board module needs to approve the
// board ask before it goes out." Every Board page is already behind Board's read, so every reader
// here may approve; the server checks again.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoneyDisplay } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  ErrorMessage,
  Icon,
  SandboxedHtmlPreview,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  testIdProp,
} from '@givmo/design-system';
import { Link2 } from 'lucide-react';
import {
  BOARD_ASK_COPY as w,
  BoardAskForm,
  BoardAskRecipientsCard,
  boardAskDraftBody,
  boardAskKeys,
  boardAskRefusal,
  fetchBoardAsk,
  fetchBoardAskEmailPreview,
  fetchBoardAskParticipation,
  fetchBoardAsks,
  organizationDate,
  sendBackBoardAsk,
  sendBoardAsk,
  updateBoardAsk,
  type BoardAsk,
  type BoardAskFormValue,
  type BoardAskParticipation,
  type BoardAskSummary,
} from '@givmo/org-console';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { Link, useParams } from '../../board-routing';
import { PageShell } from '../../components/page-shell';

/** Frame 32's words: Board's own, since the shared copy never says a thing was sent. */
const SENT_WORDS = {
  state: 'Sent',
  lede: (count: number, emailed: number, sender: string | null, day: string | null) =>
    `Sent to ${emailed < count ? `${emailed} of ${count}` : `all ${count}`} directors${sender ? ` by ${sender}` : ''}${day ? ` on ${day}` : ''}.`,
} as const;

const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** The officer pages' addresses beneath the Board mount. */
export const BOARD_ASK_PAGES = {
  list: '/asks',
  ask: '/asks/$askId',
} as const;

function dollars(amount: string | null): string | null {
  if (amount === null) return null;
  const shown = formatMoneyDisplay(amount, { cents: 'always' });
  return shown === null ? null : `$${shown}`;
}

function goal(amount: string | null): string | null {
  if (amount === null) return null;
  const shown = formatMoneyDisplay(amount, { cents: 'when-present' });
  return shown === null ? null : `$${shown}`;
}

function day(instant: string | null): string | null {
  if (instant === null) return null;
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function longDay(instant: string | null): string | null {
  if (instant === null) return null;
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function stateWords(ask: BoardAskSummary): { label: string; tone: 'neutral' | 'info' | 'success' } {
  if (ask.state === 'gone') return { label: SENT_WORDS.state, tone: 'success' };
  if (ask.state === 'submitted') return { label: w.stateSubmitted, tone: 'info' };
  return { label: w.stateDraft, tone: 'neutral' };
}

/** The Board Asks list: every ask, newest first, with where it stands. */
export function BoardAsksPage() {
  const { orgRef, isReady } = useBoardHost();
  const list = useQuery({
    queryKey: boardAskKeys.list(orgRef ?? ''),
    queryFn: ({ signal }) => fetchBoardAsks(orgRef ?? '', signal),
    enabled: isReady && orgRef !== null,
    retry: false,
  });
  return (
    <PageShell title={w.asksTitle} description={w.asksLede} testId="page-board-asks">
      {list.isPending ? (
        <Skeleton className="h-[160px] w-full rounded-xl" />
      ) : list.isError ? (
        <Card>
          <CardBody>
            <p className={PLAIN}>{w.unavailable}</p>
          </CardBody>
        </Card>
      ) : list.data.length === 0 ? (
        <Card testId="board-asks-empty">
          <CardBody>
            <p className={PLAIN}>{w.asksEmpty}</p>
          </CardBody>
        </Card>
      ) : (
        <Card flush>
          <ul className="flex flex-col divide-y divide-border-subtle">
            {list.data.map((ask) => {
              const state = stateWords(ask);
              return (
                <li key={ask.id} className="flex items-center gap-12 px-20 py-14">
                  <span className="flex min-w-0 flex-1 flex-col">
                    <Link
                      to={BOARD_ASK_PAGES.ask}
                      params={{ askId: ask.id }}
                      className="font-sans text-s14 font-w600 text-text-strong hover:underline"
                      {...testIdProp(`board-ask-row-${ask.id}`)}
                    >
                      {ask.campaignName}
                    </Link>
                    <span className="font-sans text-s13 text-text-secondary">
                      {ask.state === 'gone'
                        ? SENT_WORDS.lede(
                            ask.recipientCount,
                            ask.emailedCount,
                            ask.sentByDisplayName,
                            day(ask.sentAt),
                          )
                        : (ask.draftedByDisplayName ?? '')}
                    </span>
                  </span>
                  <Badge tone={state.tone}>{state.label}</Badge>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </PageShell>
  );
}

/** One ask: approve it while it waits, or see who's taking part once it's sent. */
export function BoardAskPage() {
  const { orgRef, isReady } = useBoardHost();
  const { askId } = useParams({ from: '/authenticated/asks/$askId' });
  const ask = useQuery({
    queryKey: boardAskKeys.one(orgRef ?? '', askId),
    queryFn: ({ signal }) => fetchBoardAsk(orgRef ?? '', askId, signal),
    enabled: isReady && orgRef !== null,
    retry: false,
  });
  if (!isReady || orgRef === null || ask.isPending) {
    return (
      <PageShell title={w.asksTitle} testId="page-board-ask">
        <Skeleton className="h-[320px] w-full rounded-xl" />
      </PageShell>
    );
  }
  if (ask.isError) {
    return (
      <PageShell title={w.asksTitle} testId="page-board-ask">
        <Card>
          <CardBody>
            <p className={PLAIN}>{w.unavailable}</p>
          </CardBody>
        </Card>
      </PageShell>
    );
  }
  if (ask.data.state === 'gone') return <TakingPart orgRef={orgRef} ask={ask.data} />;
  return <Review key={`${ask.data.id}:${ask.data.state}`} orgRef={orgRef} ask={ask.data} />;
}

function Review({ orgRef, ask }: { orgRef: string; ask: BoardAsk }) {
  const client = useQueryClient();
  const { permissions, orgName, orgTimeZone } = useBoardHost();
  const [value, setValue] = useState<BoardAskFormValue>({
    message: ask.message,
    attachments: ask.attachments,
    offersShare: ask.offersShare,
    offersPage: ask.offersPage,
  });
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // Core unread, the server still refuses at the act; the page doesn't guess.
  const live = ask.campaign === null || ask.campaign.status === 'Active';
  const pagesOpen = ask.campaign === null || ask.campaign.acceptsPages;
  const ready = value.message.trim().length > 0 && (value.offersShare || value.offersPage) && live;
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: boardAskKeys.one(orgRef, ask.id) });
    await client.invalidateQueries({ queryKey: boardAskKeys.list(orgRef) });
    await client.invalidateQueries({ queryKey: ['board-to-do'] });
  };
  const act = useMutation({
    mutationFn: async (what: 'send' | 'back') => {
      await updateBoardAsk(orgRef, ask.id, boardAskDraftBody(value));
      return what === 'send' ? sendBoardAsk(orgRef, ask.id) : sendBackBoardAsk(orgRef, ask.id);
    },
    onMutate: () => setError(null),
    onSuccess: () => refresh(),
    onError: (failure) => setError(boardAskRefusal(failure, w.sendFailed)),
  });
  // The preview is the mail as saved, so what she has changed is saved first.
  const preview = useMutation({
    mutationFn: () => updateBoardAsk(orgRef, ask.id, boardAskDraftBody(value)),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: [...boardAskKeys.one(orgRef, ask.id), 'email-preview'],
      });
      setPreviewing(true);
    },
    onError: (failure) => setError(boardAskRefusal(failure, w.saveFailed)),
  });
  return (
    <PageShell
      title={w.reviewTitle(ask.campaignName)}
      description={w.reviewLede(ask.draftedByDisplayName, longDay(ask.submittedAt ?? null))}
      testId="page-board-ask"
    >
      {!ask.factsAvailable && <p className={PLAIN}>{w.factsMissing}</p>}
      <div className="grid min-w-0 grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_300px]">
        <BoardAskForm
          orgRef={orgRef}
          orgName={orgName ?? ''}
          value={value}
          onChange={setValue}
          goalLabel={goal(ask.campaign?.goalAmount ?? null)}
          canUpload={hasBoardPermission(permissions, 'profile:write')}
          pagesOpen={pagesOpen}
          today={organizationDate(orgTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)}
        />
        <div className="flex flex-col gap-16">
          <BoardAskRecipientsCard recipients={ask.recipients} />
          <Card flush testId="board-ask-approve">
            <CardBody className="flex flex-col gap-14">
              <p className={PLAIN}>{live ? w.approvingSends : w.campaignNotLive}</p>
              <Button
                appearance="saas"
                tone="primary"
                className="self-start"
                disabled={act.isPending || ask.recipients.length === 0 || !ready}
                onClick={() => act.mutate('send')}
                testId="board-ask-approve-and-send"
              >
                {w.approveAndSend}
              </Button>
              {ask.state === 'submitted' && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  className="self-start"
                  disabled={act.isPending}
                  onClick={() => act.mutate('back')}
                  testId="board-ask-send-back"
                >
                  {w.sendBack(ask.draftedByDisplayName)}
                </Button>
              )}
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                className="self-start"
                disabled={preview.isPending}
                onClick={() => preview.mutate()}
                testId="board-ask-preview-email"
              >
                {w.previewEmail}
              </Button>
              {error !== null && <ErrorMessage testId="board-ask-error">{error}</ErrorMessage>}
            </CardBody>
          </Card>
        </div>
      </div>
      {previewing && (
        <EmailPreview orgRef={orgRef} askId={ask.id} onClose={() => setPreviewing(false)} />
      )}
    </PageShell>
  );
}

function EmailPreview({
  orgRef,
  askId,
  onClose,
}: {
  orgRef: string;
  askId: string;
  onClose: () => void;
}) {
  const preview = useQuery({
    queryKey: [...boardAskKeys.one(orgRef, askId), 'email-preview'],
    queryFn: ({ signal }) => fetchBoardAskEmailPreview(orgRef, askId, signal),
    retry: false,
  });
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content
        aria-label={w.previewEmail}
        width={720}
        className="flex flex-col gap-12"
        testId="board-ask-email-preview"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {preview.data?.subject || w.previewEmail}
        </Dialog.Title>
        {preview.data?.fromName ? (
          <p className={PLAIN} {...testIdProp('board-ask-email-from')}>
            {w.previewFrom(preview.data.fromName)}
          </p>
        ) : null}
        {preview.isPending ? (
          <Skeleton className="h-[420px] w-full rounded-lg" />
        ) : preview.isError ? (
          <p className={PLAIN}>{w.unavailable}</p>
        ) : (
          <SandboxedHtmlPreview html={preview.data.html} title={w.previewEmail} height={520} />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function TakingPart({ orgRef, ask }: { orgRef: string; ask: BoardAsk }) {
  const read = useQuery({
    queryKey: boardAskKeys.participation(orgRef, ask.id),
    queryFn: ({ signal }) => fetchBoardAskParticipation(orgRef, ask.id, signal),
    retry: false,
  });
  return (
    <PageShell
      title={w.reviewTitle(ask.campaignName)}
      description={SENT_WORDS.lede(
        ask.recipients.length,
        read.data?.participants.filter((person) => person.emailSent).length ??
          ask.recipients.length,
        ask.sentByDisplayName,
        longDay(ask.sentAt),
      )}
      testId="page-board-ask"
    >
      {read.isPending ? (
        <Skeleton className="h-[320px] w-full rounded-xl" />
      ) : read.isError ? (
        <Card>
          <CardBody>
            <p className={PLAIN}>{w.unavailable}</p>
          </CardBody>
        </Card>
      ) : (
        <Participation read={read.data} offersPage={ask.offersPage} />
      )}
    </PageShell>
  );
}

function Participation({ read, offersPage }: { read: BoardAskParticipation; offersPage: boolean }) {
  const goalLabel = goal(read.goalAmount);
  return (
    <>
      <Card flush testId="board-ask-taking-part">
        <CardHeader title={w.takingPart} subtitle={w.takingPartLede(goalLabel)} />
        <Table>
          <THead>
            <TR>
              <TH>{w.director}</TH>
              <TH>{w.opened}</TH>
              <TH>{w.shared}</TH>
              {offersPage && <TH className="text-right">{w.pageRaised}</TH>}
            </TR>
          </THead>
          <TBody>
            {read.participants.map((person) => (
              <TR key={person.boardMemberId} {...testIdProp(`taking-part-${person.boardMemberId}`)}>
                <TD className="font-w600 text-text-strong">{person.displayName}</TD>
                <TD>
                  {day(person.openedAt) ??
                    (person.notSentReason === null ? '—' : w.notEmailed(person.notSentReason))}
                </TD>
                <TD>{day(person.sharedAt) ?? '—'}</TD>
                {offersPage && (
                  <TD className="text-right">
                    {person.page === null ? (
                      <span className="text-text-tertiary">
                        {read.factsAvailable ? w.noPage : '—'}
                      </span>
                    ) : person.page.status === 'Draft' ? (
                      <span className="text-text-tertiary">{w.pageDraft}</span>
                    ) : (
                      dollars(person.page.raisedAmount)
                    )}
                  </TD>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
      {read.linkGiftCount !== null && read.linkGiftAmount !== null && (
        <p
          className="flex items-center gap-8 font-sans text-s13 font-w400 leading-h14 tracking-givmo text-text-secondary"
          {...testIdProp('board-ask-link-gifts')}
        >
          <Icon icon={Link2} size={16} aria-hidden />
          {w.linkGifts(read.linkGiftCount, dollars(read.linkGiftAmount) ?? read.linkGiftAmount)}
        </p>
      )}
      {!read.factsAvailable && <p className={PLAIN}>{w.factsMissing}</p>}
    </>
  );
}
